import { IClientInfo } from './types';
import { ThemeApiResponse } from './theme-api-types';
import { apiFetch } from './ultra-meet-config';

// Fallback theme configuration - exported for direct use
export const FALLBACK_THEME_CONFIG: IClientInfo = {
    favicon: '/images/ultraMeet/favicon.png',
    openGraph: '/images/ultraMeet/open-graph.png',
    appleTouch: '/images/ultraMeet/apple-touch.png',
    safariPinnedTab: '/images/ultraMeet/safari-pinned-tab.png',
    title: 'Ultra Meet',
    slogan: 'Secure and high quality conferences for your business',
    logo: '/images/ultraMeet/logo.png',
    logoRoom: '/images/ultraMeet/logo-room.png',
    colors: {
        brand: '#2090FF',
        background: '#313131',
        surface: '#535353',
        placeholder: '#80FFFFFF'
    },
    translations: {
        en: {
            slogan: 'Secure and high quality conferences for your business'
        },
        ru: {
            slogan: 'Безопасные и качественные конференции для вашего бизнеса'
        },
        kk: {
            slogan: 'Бизнесіңіз үшін қауіпсіз және сапалы конференциялар'
        }
    }
};

// Theme configs store support phones in display form ("+7 701 222 1125"),
// so the formatting is stripped to build a dialable tel: href
export function phoneToTelHref(phone: string): string {
    return `tel:${phone.replace(/[^\d+]/g, '')}`;
}

// Fetch theme from API (works both server-side and client-side)
export async function fetchThemeFromAPI(themeName?: string): Promise<ThemeApiResponse | null> {
    try {
        // Determine the base URL - for server-side use full URL, for client-side use relative
        const baseUrl = typeof window === 'undefined' ? process.env.API_BASE_URL || 'http://127.0.0.1:8090' : ''; // Client-side uses relative URLs

        const url = themeName ? `${baseUrl}/api/theme?name=${themeName}` : `${baseUrl}/api/theme`;

        const response = typeof window === 'undefined' ? await fetch(url) : await apiFetch(url);

        if (!response.ok) {
            console.error(`Failed to fetch theme from server: ${response.status}`);
            return null;
        }

        const data: ThemeApiResponse = await response.json();

        if (data.result === 'success') {
            return data;
        }

        return null;
    } catch (error) {
        console.error('Error fetching theme config from server:', error);
        return null;
    }
}

// Shared getServerSideProps theme fetching logic
export async function getThemeForSSR(req: any): Promise<IClientInfo> {
    // Check for theme preference in cookies
    const cookieString = req.headers.cookie || '';
    const preferredTheme = getThemePreferenceFromCookieString(cookieString);

    // Try to get theme from API first, fallback to local config
    let initialTheme: IClientInfo | null = null;

    // First try to fetch from server API (unified source of truth)
    try {
        const apiResponse = await fetchThemeFromAPI(preferredTheme || undefined);
        if (apiResponse && apiResponse.theme) {
            initialTheme = apiResponse.theme;
        }
    } catch (error) {
        console.error('Failed to fetch theme from server API:', error);
    }

    // Fallback to built-in theme if API fails
    return initialTheme || FALLBACK_THEME_CONFIG;
}

// Import the cookie utility function for the shared function
import { getThemePreferenceFromCookieString } from './theme-cookies';
